import type {
  Location,
  EncounterResponse,
} from '@claim-os/shared';
import { apiClient, locationToWire, codingToWire } from '@claim-os/shared';
import type { SubmitClaimForm } from '../schema';
import { invalidateClaimsCache } from '../../claims-list/services/claimsService';

/**
 * Build the multipart FormData for `POST /api/v1/encounters` from the
 * submit-claim form state. Coding moved into each `cases[]` entry (migration 018).
 *  - `patient`, `cases` are JSON-encoded text parts (each case carries its own `coding`).
 *  - `dateOfService`, `department` are plain text parts.
 *  - `location` is a JSON-encoded LocationObject part.
 *  - Each attached file becomes a separate binary `files` part.
 *  - Per-provider `role` is dropped when it equals 'DEFAULT' (the wire treats
 *    "no role" as the implicit default); `credential` and `npi` are carried through
 *    when the directory supplied one (feature 044).
 *  - Each case's coding passes through `codingToWire`, which folds the four
 *    anesthesia window fields into the two persisted instants (feature 044).
 *
 * `patient` carries the intake demographics — `firstName`, `lastName`, and any
 * of `dateOfBirth`, `insurer`, `memberId`, `medicalRecordNumber` that were
 * entered; blank optional fields are omitted to keep the JSONB compact.
 * `dateOfService` and `location` are likewise sent only when present.
 */
export function buildCreateEncounterFormData(form: SubmitClaimForm, qbLocations: Location[]): FormData {
  const fd = new FormData();

  // Patient — include all intake fields; omit blanks so the JSONB stays compact.
  const patient: Record<string, string> = {
    firstName: form.firstName,
    lastName: form.lastName,
  };
  if (form.dateOfBirth) patient.dateOfBirth = form.dateOfBirth;
  if (form.insurer) patient.insurer = form.insurer;
  if (form.memberId) patient.memberId = form.memberId;
  if (form.medicalRecordNumber) patient.medicalRecordNumber = form.medicalRecordNumber;
  fd.append('patient', JSON.stringify(patient));

  if (form.dateOfService) {
    fd.append('dateOfService', form.dateOfService);
  }
  if (form.location) {
    fd.append('location', JSON.stringify(locationToWire(qbLocations, form.location)));
  }

  fd.append('department', form.isGapCase ? 'GAP' : 'STANDARD');

  fd.append(
    'cases',
    JSON.stringify(
      form.claimTypes.map((ct) => ({
        billType: ct.type,
        providers: ct.providers.map((p) => ({
          providerId: p.providerId,
          providerName: p.providerName,
          ...(p.role && p.role !== 'DEFAULT' ? { role: p.role } : {}),
          // The credential decides anesthesia role eligibility and is rendered
          // alongside the name, so it has to survive the submit (feature 044).
          ...(p.credential ? { credential: p.credential } : {}),
          // NPI lets SharePointAdapter match this provider against the SharePoint
          // master file exactly, instead of falling back to name-token matching,
          // which misses real providers on name-format mismatches.
          ...(p.npi ? { npi: p.npi } : {}),
        })),
        // Per-claim coding (ICD-10 + CPT + ASA). FACILITY carries none — the
        // server normalises it to an empty object regardless.
        coding: codingToWire(ct.coding),
      })),
    ),
  );

  for (const file of form.documents) {
    fd.append('files', file, file.name);
  }
  return fd;
}

/**
 * Submit a new patient encounter to encounter-service. POSTs `multipart/form-data`
 * to `/api/v1/encounters` — see data-model.md §2.5 for the part list. On success
 * (HTTP 201) the claims-list cache is invalidated so the new encounter's cases
 * appear in the list on next render.
 *
 * The whole submission is all-or-nothing per 024 FR-003: either the encounter +
 * all its files commit, or nothing does. There is no per-file retry.
 */
export async function submitEncounter(
  form: SubmitClaimForm,
  qbLocations: Location[],
): Promise<EncounterResponse> {
  const fd = buildCreateEncounterFormData(form, qbLocations);
  // Note: do NOT set Content-Type. axios infers `multipart/form-data; boundary=...`
  // from the FormData body.
  const response = await apiClient.post<EncounterResponse>('/api/v1/encounters', fd);
  invalidateClaimsCache();
  return response.data;
}
